import type { CSSProperties, FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface DonutSegment {
    name: string
    value: number
    color: string
}

interface DonutChartProps {
    title: string
    segments: Array<DonutSegment>
    size?: number
    thickness?: number
    centerLabel?: string
    formatValue?: (value: number) => string
    legend?: 'side' | 'below' | 'none'
    interactive?: boolean
    animation?: ChartAnimation
    delay?: number
    surface?: 'light' | 'dark'
    className?: string
}

const defaultFormat = (value: number): string => value.toLocaleString('en-US')

const RADIUS = 42
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const GAP = 1.4

export const DonutChart: FC<DonutChartProps> = ({
    title,
    segments,
    size = 160,
    thickness = 11,
    centerLabel = 'Total',
    formatValue = defaultFormat,
    legend = 'side',
    interactive = true,
    animation = 'inview',
    delay = 0,
    surface = 'light',
    className
}): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const total = segments.reduce((sum, segment) => sum + segment.value, 0)
    const isDark = surface === 'dark'

    let offset = 0

    const arcs = segments.map((segment, index) => {
        const length = total ? (segment.value / total) * CIRCUMFERENCE : 0
        const dash = Math.max(0, length - GAP)
        const arc = { ...segment, index, dash, offset }

        offset += length

        return arc
    })

    const focused = active !== null ? segments[active] : null
    const share = (value: number): string => `${total ? ((value / total) * 100).toFixed(0) : 0}%`

    return (
        <div className={cn('@container min-w-0', className)}>
        <figure
            className={cn(
                'flex items-center',
                legend === 'side' ? 'flex-col gap-5 @md:flex-row @md:gap-6' : 'flex-col gap-6',
                animation === 'autoplay' && 'chart-autoplay'
            )}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
        >
            <div className='relative shrink-0' style={{ width: size, height: size }}>
                <svg aria-hidden='true' focusable='false' viewBox='0 0 100 100' className='size-full -rotate-90'>
                    <circle
                        cx='50'
                        cy='50'
                        r={RADIUS}
                        fill='none'
                        strokeWidth={thickness}
                        className={isDark ? 'stroke-cream/10' : 'stroke-burgundy/[0.06]'}
                    />

                    {arcs.map(arc => (
                        <circle
                            key={arc.name}
                            data-arc=''
                            cx='50'
                            cy='50'
                            r={RADIUS}
                            fill='none'
                            stroke={arc.color}
                            strokeWidth={active === arc.index ? thickness + 2 : thickness}
                            strokeDashoffset={-arc.offset}
                            className={cn('chart-arc transition-[stroke-width,opacity] duration-300', active !== null && active !== arc.index && 'opacity-35')}
                            style={{
                                '--arc-dash': `${arc.dash.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`,
                                '--arc-circ': CIRCUMFERENCE.toFixed(2),
                                '--chart-delay': `${delay + arc.index * 90}ms`
                            } as CSSProperties}
                            onPointerEnter={interactive ? () => setActive(arc.index) : undefined}
                            onPointerLeave={interactive ? () => setActive(null) : undefined}
                        />
                    ))}
                </svg>

                <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center'>
                    <span className={cn('font-mono uppercase', size < 120 ? 'text-[8px] tracking-[0.12em]' : 'text-[10px] tracking-[0.14em]', isDark ? 'text-cream/55' : 'text-stone')}>
                        {focused ? focused.name : centerLabel}
                    </span>

                    <span className={cn('mt-1 font-heading font-medium tabular-nums tracking-tight', size < 120 ? 'text-[13px] leading-none' : 'text-lg', isDark ? 'text-cream' : 'text-burgundy')}>
                        {formatValue(focused ? focused.value : total)}
                    </span>

                    {focused && (
                        <span className={cn('text-[11px] tabular-nums', isDark ? 'text-cream/60' : 'text-stone-dark')}>
                            {share(focused.value)} of total
                        </span>
                    )}
                </div>
            </div>

            {legend !== 'none' && (
                <ul className={cn('w-full min-w-0 space-y-1.5', legend === 'side' && '@md:flex-1')}>
                    {segments.map((segment, index) => (
                        <li
                            key={segment.name}
                            className={cn(
                                'flex items-center gap-2.5 rounded-lg px-2 py-1 text-xs transition-colors duration-300',
                                active === index && (isDark ? 'bg-cream/[0.06]' : 'bg-burgundy/[0.04]')
                            )}
                            onPointerEnter={interactive ? () => setActive(index) : undefined}
                            onPointerLeave={interactive ? () => setActive(null) : undefined}
                        >
                            <span aria-hidden='true' className='size-2.5 shrink-0 rounded-[3px]' style={{ backgroundColor: segment.color }} />
                            <span className={cn('min-w-0 flex-1 truncate', isDark ? 'text-cream/75' : 'text-stone-dark')}>{segment.name}</span>
                            <span className={cn('shrink-0 tabular-nums', isDark ? 'text-cream/55' : 'text-stone')}>{share(segment.value)}</span>
                            <span className={cn('w-14 shrink-0 text-right font-medium tabular-nums', isDark ? 'text-cream' : 'text-burgundy')}>{formatValue(segment.value)}</span>
                        </li>
                    ))}
                </ul>
            )}

            <figcaption className='sr-only'>
                {title}: {segments.map(segment => `${segment.name} ${formatValue(segment.value)} (${share(segment.value)})`).join(', ')}
            </figcaption>
        </figure>
        </div>
    )
}
